<template>
  <div class="aml-alerts-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.compliance.aml_alerts.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.compliance.aml_alerts.subtitle') }}</p>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="filter-bar">
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.aml_alerts.filter_search') }}</label>
        <input
          v-model="filters.search"
          type="text"
          class="filter-input"
          :placeholder="$t('admin.compliance.aml_alerts.filter_search')"
          @input="debouncedFetch"
        />
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.aml_alerts.filter_status') }}</label>
        <select v-model="filters.status" @change="resetAndFetch">
          <option value="">{{ $t('admin.compliance.aml_alerts.status_all') }}</option>
          <option value="open">{{ $t('admin.compliance.aml_alerts.status_open') }}</option>
          <option value="reviewed">{{ $t('admin.compliance.aml_alerts.status_reviewed') }}</option>
          <option value="escalated">{{ $t('admin.compliance.aml_alerts.status_escalated') }}</option>
          <option value="dismissed">{{ $t('admin.compliance.aml_alerts.status_dismissed') }}</option>
          <option value="sar_filed">{{ $t('admin.compliance.aml_alerts.status_sar_filed') }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.aml_alerts.filter_severity') }}</label>
        <select v-model="filters.severity" @change="resetAndFetch">
          <option value="">{{ $t('admin.compliance.aml_alerts.severity_all') }}</option>
          <option value="critical">{{ $t('admin.compliance.aml_alerts.severity_critical') }}</option>
          <option value="high">{{ $t('admin.compliance.aml_alerts.severity_high') }}</option>
          <option value="medium">{{ $t('admin.compliance.aml_alerts.severity_medium') }}</option>
          <option value="low">{{ $t('admin.compliance.aml_alerts.severity_low') }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.aml_alerts.filter_rule') }}</label>
        <select v-model="filters.rule_type" @change="resetAndFetch">
          <option value="">{{ $t('admin.compliance.aml_alerts.rule_all') }}</option>
          <option value="deposit_daily_threshold">{{ $t('admin.compliance.aml_alerts.rule_daily_deposit') }}</option>
          <option value="deposit_monthly_threshold">{{ $t('admin.compliance.aml_alerts.rule_monthly_deposit') }}</option>
          <option value="pep_match">{{ $t('admin.compliance.aml_alerts.rule_pep_match') }}</option>
          <option value="velocity">{{ $t('admin.compliance.aml_alerts.rule_velocity') }}</option>
          <option value="manual">{{ $t('admin.compliance.aml_alerts.rule_manual') }}</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <span class="spinner"></span>
      {{ $t('admin.compliance.aml_alerts.loading') }}
    </div>

    <!-- Alerts Table -->
    <div v-else class="table-container">
      <table>
        <thead>
          <tr>
            <th class="col-id">{{ $t('admin.compliance.aml_alerts.col_id') }}</th>
            <th class="col-player">{{ $t('admin.compliance.aml_alerts.col_player') }}</th>
            <th class="col-rule">{{ $t('admin.compliance.aml_alerts.col_rule_type') }}</th>
            <th class="col-severity">{{ $t('admin.compliance.aml_alerts.col_severity') }}</th>
            <th class="col-status">{{ $t('admin.compliance.aml_alerts.col_status') }}</th>
            <th class="col-trigger">{{ $t('admin.compliance.aml_alerts.col_trigger_summary') }}</th>
            <th class="col-date sortable" :class="sortClass('created_at')" @click="toggleSort('created_at')">
              {{ $t('admin.compliance.aml_alerts.col_created') }}
              <span class="sort-icon">{{ sortIcon('created_at') }}</span>
            </th>
            <th class="col-actions">{{ $t('admin.compliance.aml_alerts.col_actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="alerts.length === 0">
            <td colspan="8" class="empty-row">{{ $t('admin.compliance.aml_alerts.empty') }}</td>
          </tr>
          <tr
            v-for="alert in alerts"
            :key="alert.id"
            class="alert-row"
            @click="openDetailModal(alert)"
          >
            <td class="mono-value">#{{ alert.id }}</td>
            <td>
              <div class="player-info">
                <div class="player-avatar">{{ getInitials(alert.username ?? '') }}</div>
                <div>
                  <div class="player-name">{{ alert.username }}</div>
                  <div class="player-sub">{{ alert.email }}</div>
                </div>
              </div>
            </td>
            <td>
              <span class="rule-tag">{{ formatRuleType(alert.rule_type) }}</span>
            </td>
            <td>
              <span class="badge" :class="severityBadgeClass(alert.severity)">{{ alert.severity }}</span>
            </td>
            <td>
              <span class="badge" :class="statusBadgeClass(alert.status)">{{ alert.status }}</span>
            </td>
            <td class="trigger-cell">{{ alert.trigger_summary }}</td>
            <td>{{ formatDate(alert.created_at) }}</td>
            <td>
              <div class="action-buttons" @click.stop>
                <button class="btn-icon" title="View Details" @click="openDetailModal(alert)">
                  <AppIcon name="action-view" :size="14" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="totalPages > 0" class="pagination">
        <button
          class="pagination-btn"
          :disabled="currentPage <= 1"
          @click="goToPage(currentPage - 1)"
        >&larr;</button>
        <button
          v-for="page in visiblePages"
          :key="page"
          class="pagination-btn"
          :class="{ active: page === currentPage }"
          @click="goToPage(page)"
        >{{ page }}</button>
        <span class="pagination-info">
          {{ $t('admin.compliance.aml_alerts.showing', { start: rangeStart, end: rangeEnd, total: totalAlerts }) }}
        </span>
        <button
          class="pagination-btn"
          :disabled="currentPage >= totalPages"
          @click="goToPage(currentPage + 1)"
        >&rarr;</button>
      </div>
    </div>

    <!-- Alert Detail / Review Modal -->
    <div v-if="detailModal.visible" class="modal-overlay" @click.self="closeDetailModal">
      <div class="modal-content modal-wide">
        <div class="modal-header">
          <h3 class="modal-title">{{ $t('admin.compliance.aml_alerts.modal_title', { id: detailModal.alert?.id }) }}</h3>
          <button class="modal-close" @click="closeDetailModal">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Alert Info -->
          <div class="detail-grid">
            <div class="detail-item">
              <div class="detail-label">{{ $t('admin.compliance.aml_alerts.detail_player') }}</div>
              <div class="detail-value">
                <router-link
                  :to="`/admin/players/${detailModal.alert?.user_id}`"
                  class="player-link"
                  @click.stop
                >{{ detailModal.alert?.username }}</router-link>
                <span class="player-email-sub">{{ detailModal.alert?.email }}</span>
              </div>
            </div>
            <div class="detail-item">
              <div class="detail-label">{{ $t('admin.compliance.aml_alerts.detail_rule_type') }}</div>
              <div class="detail-value">{{ formatRuleType(detailModal.alert?.rule_type ?? '') }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">{{ $t('admin.compliance.aml_alerts.detail_severity') }}</div>
              <div class="detail-value">
                <span class="badge" :class="severityBadgeClass(detailModal.alert?.severity ?? '')">{{ detailModal.alert?.severity }}</span>
              </div>
            </div>
            <div class="detail-item">
              <div class="detail-label">{{ $t('admin.compliance.aml_alerts.detail_status') }}</div>
              <div class="detail-value">
                <span class="badge" :class="statusBadgeClass(detailModal.alert?.status ?? '')">{{ detailModal.alert?.status }}</span>
              </div>
            </div>
            <div class="detail-item">
              <div class="detail-label">{{ $t('admin.compliance.aml_alerts.detail_created') }}</div>
              <div class="detail-value">{{ formatDateTime(detailModal.alert?.created_at ?? '') }}</div>
            </div>
            <div v-if="detailModal.alert?.reviewed_at" class="detail-item">
              <div class="detail-label">{{ $t('admin.compliance.aml_alerts.detail_reviewed') }}</div>
              <div class="detail-value">{{ formatDateTime(detailModal.alert?.reviewed_at ?? '') }}</div>
            </div>
          </div>

          <!-- Trigger Details -->
          <div class="detail-section">
            <div class="detail-section-title">{{ $t('admin.compliance.aml_alerts.section_trigger') }}</div>
            <div class="trigger-details-box">
              <div v-for="(value, key) in detailModal.alert?.trigger_details" :key="key" class="trigger-detail-row">
                <span class="trigger-key">{{ formatTriggerKey(String(key)) }}</span>
                <span class="trigger-value">{{ formatTriggerValue(String(key), value) }}</span>
              </div>
            </div>
          </div>

          <!-- Previous Review Notes -->
          <div v-if="detailModal.alert?.review_notes" class="detail-section">
            <div class="detail-section-title">{{ $t('admin.compliance.aml_alerts.section_notes') }}</div>
            <div class="previous-notes">{{ detailModal.alert.review_notes }}</div>
          </div>

          <!-- Review Form (only for actionable alerts) -->
          <div v-if="isReviewable" class="detail-section">
            <div class="detail-section-title">{{ $t('admin.compliance.aml_alerts.section_review') }}</div>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.compliance.aml_alerts.form_new_status') }} <span class="required">*</span></label>
              <select v-model="reviewForm.status" class="form-select">
                <option value="">Select status...</option>
                <option value="reviewed">{{ $t('admin.compliance.aml_alerts.status_reviewed') }}</option>
                <option value="escalated">{{ $t('admin.compliance.aml_alerts.status_escalated') }}</option>
                <option value="dismissed">{{ $t('admin.compliance.aml_alerts.status_dismissed') }}</option>
                <option value="sar_filed">{{ $t('admin.compliance.aml_alerts.status_sar_filed') }}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.compliance.aml_alerts.form_notes') }}</label>
              <textarea
                v-model="reviewForm.notes"
                class="form-textarea"
                placeholder="Add notes about your review..."
                rows="3"
                maxlength="1000"
              ></textarea>
              <div class="char-count">{{ reviewForm.notes.length }}/1000</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" :disabled="reviewForm.processing" @click="closeDetailModal">{{ $t('admin.compliance.aml_alerts.btn_close') }}</button>
          <button
            v-if="isReviewable"
            class="btn btn-primary"
            :disabled="!reviewForm.status || reviewForm.processing"
            @click="submitReview"
          >
            <span v-if="reviewForm.processing" class="spinner spinner-sm"></span>
            {{ reviewForm.processing ? $t('admin.compliance.aml_alerts.btn_submitting') : $t('admin.compliance.aml_alerts.btn_submit') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const toast = useToastStore()

// ----------- Types -----------

interface AmlAlertItem {
  id: number
  user_id: number
  username: string | null
  email: string | null
  rule_type: string
  severity: string
  status: string
  trigger_details: Record<string, unknown>
  trigger_summary: string
  reviewed_by: number | null
  review_notes: string | null
  reviewed_at: string | null
  created_at: string
}

interface PaginatedResponse {
  data: AmlAlertItem[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

// ----------- State -----------

const alerts = ref<AmlAlertItem[]>([])
const loading = ref(true)
const currentPage = ref(1)
const totalPages = ref(0)
const totalAlerts = ref(0)

const filters = ref({
  search: '',
  status: '',
  severity: '',
  rule_type: '',
})

const sortField = ref('created_at')
const sortDirection = ref<'asc' | 'desc'>('desc')

// Detail modal
const detailModal = reactive({
  visible: false,
  alert: null as AmlAlertItem | null,
})

const reviewForm = reactive({
  status: '',
  notes: '',
  processing: false,
})

// ----------- Computed -----------

const rangeStart = computed(() => {
  if (totalAlerts.value === 0) return 0
  return (currentPage.value - 1) * 25 + 1
})

const rangeEnd = computed(() => {
  return Math.min(currentPage.value * 25, totalAlerts.value)
})

const visiblePages = computed(() => {
  const pages: number[] = []
  const total = totalPages.value
  const current = currentPage.value

  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)

  if (end - start < 4) {
    if (start === 1) {
      end = Math.min(total, start + 4)
    } else {
      start = Math.max(1, end - 4)
    }
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

const isReviewable = computed(() => {
  if (!detailModal.alert) return false
  const s = detailModal.alert.status
  return s === 'open' || s === 'escalated'
})

// ----------- Data Fetching -----------

let debounceTimer: ReturnType<typeof setTimeout> | null = null

function debouncedFetch() {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    currentPage.value = 1
    fetchAlerts()
  }, 300)
}

function resetAndFetch() {
  currentPage.value = 1
  fetchAlerts()
}

async function fetchAlerts(): Promise<void> {
  loading.value = true
  try {
    const params: Record<string, string | number> = {
      page: currentPage.value,
      sort: sortField.value,
      direction: sortDirection.value,
    }

    if (filters.value.search) params.search = filters.value.search
    if (filters.value.status) params.status = filters.value.status
    if (filters.value.severity) params.severity = filters.value.severity
    if (filters.value.rule_type) params.rule_type = filters.value.rule_type

    const response = await apiClient.get<PaginatedResponse>('/admin/api/aml-alerts', { params })
    const data = response.data

    alerts.value = data.data
    currentPage.value = data.current_page
    totalPages.value = data.last_page
    totalAlerts.value = data.total
  } catch {
    toast.error(t('admin.compliance.aml_alerts.error_load'))
  } finally {
    loading.value = false
  }
}

// ----------- Review -----------

async function submitReview(): Promise<void> {
  if (!detailModal.alert || !reviewForm.status) return

  reviewForm.processing = true
  try {
    await apiClient.post(`/admin/api/aml-alerts/${detailModal.alert.id}/review`, {
      status: reviewForm.status,
      review_notes: reviewForm.notes || null,
    })
    toast.success(t('admin.compliance.aml_alerts.success_review'))
    closeDetailModal()
    fetchAlerts()
  } catch {
    toast.error(t('admin.compliance.aml_alerts.error_review'))
  } finally {
    reviewForm.processing = false
  }
}

// ----------- Modal -----------

function openDetailModal(alert: AmlAlertItem) {
  detailModal.alert = alert
  detailModal.visible = true
  reviewForm.status = ''
  reviewForm.notes = ''
  reviewForm.processing = false
}

function closeDetailModal() {
  if (reviewForm.processing) return
  detailModal.visible = false
  detailModal.alert = null
}

// ----------- Sorting -----------

function toggleSort(field: string) {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDirection.value = 'desc'
  }
  currentPage.value = 1
  fetchAlerts()
}

function sortClass(field: string): string {
  return sortField.value === field ? 'sorted' : ''
}

function sortIcon(field: string): string {
  if (sortField.value !== field) return '\u2195'
  return sortDirection.value === 'asc' ? '\u2191' : '\u2193'
}

// ----------- Formatting -----------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function getInitials(username: string): string {
  if (!username) return '?'
  return username
    .split(/[_.\-\s]/)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function formatRuleType(ruleType: string): string {
  const map: Record<string, string> = {
    deposit_daily_threshold: t('admin.compliance.aml_alerts.rule_daily_deposit'),
    deposit_monthly_threshold: t('admin.compliance.aml_alerts.rule_monthly_deposit'),
    pep_match: t('admin.compliance.aml_alerts.rule_pep_match'),
    velocity: t('admin.compliance.aml_alerts.rule_velocity'),
    manual: t('admin.compliance.aml_alerts.rule_manual'),
  }
  return map[ruleType] ?? ruleType
}

function severityBadgeClass(severity: string): string {
  switch (severity) {
    case 'critical': return 'badge-critical'
    case 'high': return 'badge-high'
    case 'medium': return 'badge-medium'
    case 'low': return 'badge-low'
    default: return 'badge-low'
  }
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'open': return 'badge-open'
    case 'reviewed': return 'badge-reviewed'
    case 'escalated': return 'badge-escalated'
    case 'dismissed': return 'badge-dismissed'
    case 'sar_filed': return 'badge-sar'
    default: return 'badge-open'
  }
}

function formatTriggerKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatTriggerValue(key: string, value: unknown): string {
  if ((key === 'threshold_amount' || key === 'actual_amount') && typeof value === 'number') {
    return '$' + (value / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value)
  }
  return String(value ?? 'N/A')
}

// ----------- Pagination -----------

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchAlerts()
}

// ----------- Lifecycle -----------

onMounted(() => {
  fetchAlerts()
})
</script>

<style scoped>
.aml-alerts-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

/* Filter Bar */
.filter-bar {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  white-space: nowrap;
}

.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  width: 180px;
  transition: border-color 150ms ease;
}

.filter-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.filter-bar select {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  width: 140px;
  transition: border-color 150ms ease;
}

.filter-bar select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-bar select option {
  background: var(--bg-secondary, #1a2c38);
  color: var(--text-primary, #fff);
}

/* Loading */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 64px 20px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-sm {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  transition: border-color 150ms ease;
}

.table-container:hover {
  border-color: rgba(255, 255, 255, 0.1);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-tertiary, #213743);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

th.sortable {
  cursor: pointer;
  user-select: none;
  transition: color 150ms ease;
}

th.sortable:hover {
  color: var(--text-primary, #fff);
}

th.sorted {
  color: var(--accent-primary, #00e701);
}

.sort-icon {
  font-size: 10px;
  margin-left: 4px;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

.alert-row {
  background: var(--bg-secondary, #1a2c38);
  transition: background 150ms ease;
  cursor: pointer;
}

.alert-row:hover {
  background: #253d4f;
}

tbody tr:last-child td {
  border-bottom: none;
}

.empty-row {
  text-align: center;
  color: var(--text-tertiary, #7f8fa4);
  padding: 48px 16px !important;
}

.mono-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-primary, #fff);
}

/* Player Cell */
.player-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.player-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1475e1, #9b59b6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.player-name {
  font-size: 13px;
  color: var(--text-primary, #fff);
  font-weight: 600;
}

.player-sub {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Rule Tag */
.rule-tag {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  background: var(--bg-tertiary, #213743);
  padding: 4px 10px;
  border-radius: 9999px;
  white-space: nowrap;
}

/* Trigger Cell */
.trigger-cell {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

/* Severity badges */
.badge-critical {
  background: rgba(237, 66, 69, 0.2);
  color: #ed4245;
}

.badge-high {
  background: rgba(255, 152, 0, 0.2);
  color: #ff9800;
}

.badge-medium {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-low {
  background: rgba(127, 143, 164, 0.15);
  color: var(--text-tertiary, #7f8fa4);
}

/* Status badges */
.badge-open {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-reviewed {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-escalated {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.badge-dismissed {
  background: rgba(127, 143, 164, 0.15);
  color: var(--text-tertiary, #7f8fa4);
}

.badge-sar {
  background: rgba(20, 117, 225, 0.15);
  color: var(--accent-secondary, #1475e1);
}

/* Actions */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  font-size: 14px;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-icon:hover {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled) {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 12px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  background: #00cc01;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.15s ease;
}

.modal-content {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg, 12px);
  width: 100%;
  max-width: 500px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-wide {
  max-width: 600px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
  font-family: inherit;
}

.modal-close:hover {
  color: var(--text-primary, #fff);
}

.modal-body {
  padding: 20px 24px;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* Detail Grid */
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
}

.detail-value {
  font-size: 14px;
  color: var(--text-primary, #fff);
}

.player-link {
  color: var(--accent-secondary, #1475e1);
  text-decoration: none;
  font-weight: 600;
}

.player-link:hover {
  text-decoration: underline;
}

.player-email-sub {
  display: block;
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 2px;
}

/* Detail Sections */
.detail-section {
  margin-bottom: 20px;
}

.detail-section-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.trigger-details-box {
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  padding: 12px 16px;
}

.trigger-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.trigger-detail-row:last-child {
  border-bottom: none;
}

.trigger-key {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

.trigger-value {
  font-size: 13px;
  color: var(--text-primary, #fff);
  font-weight: 600;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
}

.previous-notes {
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  padding: 12px 16px;
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.5;
}

/* Form */
.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 6px;
}

.required {
  color: var(--color-danger, #ed4245);
}

.form-select {
  width: 100%;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 150ms ease;
}

.form-select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-select option {
  background: var(--bg-secondary, #1a2c38);
  color: var(--text-primary, #fff);
}

.form-textarea {
  width: 100%;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 150ms ease;
}

.form-textarea:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-textarea::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.char-count {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  text-align: right;
  margin-top: 4px;
}

/* Column widths */
.col-id { width: 6%; }
.col-player { width: 20%; }
.col-rule { width: 14%; }
.col-severity { width: 10%; }
.col-status { width: 10%; }
.col-trigger { width: 22%; }
.col-date { width: 12%; }
.col-actions { width: 6%; }

/* Responsive */
@media (max-width: 1200px) {
  .filter-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  table {
    font-size: 12px;
  }

  td,
  th {
    padding: 10px 12px;
  }
}

@media (max-width: 768px) {
  .aml-alerts-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
